import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import { Box, Button, Grid, IconButton, Stack, Tooltip } from '@mui/material';
import { GridColDef, GridRowId, GridSortModel } from '@mui/x-data-grid';
import { isEmpty } from 'lodash-es';
import { useState, useEffect, useCallback } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useSearchParams } from 'react-router-dom';
import {
  ClientContactDTO,
  deleteClientContact,
  ColumnSettings,
  updateClientContactStatus,
  LogAction,
  LogContextType,
  LogEntityType,
  saveActivityLog,
  DncDetailsDTO,
  assignDncDetails,
  ClientRequestDTO,
  getAllContacts,
} from 'wisestep-api';
import {
  EmptyTeamsAndUsersIcon,
  WsButton,
  WsDrawer,
  WsIconLinkedIn,
  WsListingLoader,
  WsTypography,
  WsTypoVariants,
  WsIconMoreVer,
  WsIconText,
  WsTypoColors,
  getDateUsingFormat,
  IUserDTO,
  FieldOption,
  WsDataGrid,
} from 'wisestep-base';

import { ConfirmDeleteDialog } from '../../../../components';
import NoDataFoundCard from '../../../../components/noDataFoundCard';
import { IBreadCrumbsList } from '../../../../components/WsBreadCrumbs';
import { useUserContext, useWorkspaceContext } from '../../../../context';
import { activityLogDTOHelper } from '../../../../features/activityLog/ActivityHelper';
import {
  contactsDefaultFilterValues,
  defaultColumnSettings,
  IFilters,
} from '../../../../features/clientContacts/helper/ContactListingHelper';
import PopoverOnClick from '../../../../features/clientListingPage/components/PopoverOnClick';
import DataPrivacy from '../../../../features/conversationWidget/components/DataPrivacy';
import { useClientAuthz, ClientAction } from '../../../../hooks/useClientAuthz';
import { useDialog } from '../../../../hooks/useDialog';
import { useDrawer } from '../../../../hooks/useDrawer';
import ErrorLogger from '../../../../providers/ErrorLogger';
import getDecodedQueryParams from '../../../../utils/getDecodedQueryParams';
import getEncodedQueryParams from '../../../../utils/getEncodedQueryParams';
import { failed, success } from '../../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../../utils/NotificationStore';
import { openUrlInNewTab } from '../../../../utils/openUrlInNewTab';
import ClientContacts from '../../../clientListingPage/components/ClientContacts';
import { BottomBlueBar, StyledGrid } from '../../../clientListingPage/components/ClientList';
import ClientOwnerPopUp from '../../../clientListingPage/components/ClientOwnerPopUp';
import {
  defaultClientContactDTO,
  fetchColumnSettings,
  getEncodedBreadCrumbs,
  saveColumnSettingsToDB,
} from '../../../clientListingPage/helper/ClientListingHelper';
import { defaultQuery } from '../../../jobs/listing/JObSearchFilterDefaultQuery';
import WsLink from '../../../teams/components/WsLink';
import {
  clientsAmplitudeEvents,
  ClintPageBreadCrumb,
  createClientContactOwnerActivityLog,
  fetchNameAndLinkedUrlsOfClientContact,
  limitedPrivacyOptions,
} from '../../helper/clientDetailsHelper';
import openNotesFormDialog from '../../helper/openNotesFormDialog';
import { useClientStore } from '../../store/useClientStore';
import AddEntityButton from '../addEntityButton/AddEntityButton';
import ClientSharingTable from '../clientSharing/ClientSharingTable';
import ClientUserList from '../clientSharing/ClientUserList';
import { ILegalState, initialState } from '../legalIdentifiersTab/LegalIdentifiersTab';

import ContactFilters from './ContactFilters';

interface IClientContactsTab {
  clientId: string;
  clientName: string;
  parentName: string;
  totalClientContactCount: number;
  tags: string[];
}

const ClientContactsTab = ({ clientId, parentName, tags }: IClientContactsTab) => {
  const loggedInUser = useUserContext().userDTO as IUserDTO;
  const [clientContacts, setClientContacts] = useState<ClientContactDTO[]>([]);
  const [openDialogs, setOpenDialogs] = useState<ILegalState>(initialState);
  const [totalRecord, setTotalRecord] = useState<number>(0);
  const [searchQuery, setSearchQuery] = useState<IFilters>(contactsDefaultFilterValues);
  const [selectedClientContact, setSelectedClientContact] = useState<ClientContactDTO>();
  const [loader, setLoader] = useState<boolean>(false);
  const setNotification = useNotificationStore((state) => state.setNotification);
  const [pageSize, setPageSize] = useState<number>(50);
  const [settings, setSettings] = useState<FieldOption[]>(defaultColumnSettings);
  const tableData: Record<string, unknown>[] = [];
  const workspaceContext = useWorkspaceContext();
  const owns = workspaceContext.owns;
  const [clientContactId, setClientContactId] = useState<string>();
  const [param] = useSearchParams();
  const isChild: boolean = param.get('isChild') === 'true';
  const queryData = param.get('d') ?? '';
  const [open, setOpen] = useState(false);
  const reload = () => setOpen(false);
  const [pageNo, setPageNo] = useState<number>(0);
  const [openDrawer, closeDrawer] = useDrawer();
  const [rowSelectionModel, setRowSelectionModel] = useState<GridRowId[]>([]);
  const [openDialog, closeDialog] = useDialog();
  const clintName = useClientStore((state) => state.clientName);
  const [selectedContact, setSelectedContact] = useState<ClientContactDTO | null>(null);

  const isAuthorizedAddContact = useClientAuthz(ClientAction.ADD_VIEW_USER_CONTACT_DETAILS);
  const isAuthorizedEditContact = useClientAuthz(ClientAction.EDIT_VIEW_USER_CONTACT_DETAILS);
  const isAuthorizedAddAssignClientContact = useClientAuthz(ClientAction.ADD_ASSIGN_CLIENT_CONTACT);

  const [sortModel, setSortModel] = useState<GridSortModel>([{ field: 'firstName', sort: 'asc' }]);
  const [openDNC, setOpenDNC] = useState<boolean>(false);
  const [openClientOwnerFormDialog, setOpenClientOwnerFormDialog] = useState<boolean>(false);

  const getClientContacts = useCallback(async () => {
    try {
      if (clientId) {
        setLoader(true);
        const contactStatus = searchQuery.status ?? ['Active'];
        const sortBy = !isEmpty(sortModel) ? sortModel[0].field : '';
        const sortOrder = !isEmpty(sortModel) ? sortModel[0].sort : '';
        const payload: ClientRequestDTO = {
          pageNo: pageNo,
          pageSize: pageSize,
          query: searchQuery.search ?? '',
          sortBy: sortBy,
          sortOrder: sortOrder ?? '',
          status: contactStatus,
          clientIds: [clientId],
          clientTags: searchQuery.tags ?? [],
        };
        const response = await getAllContacts(payload);
        setClientContacts(response.data.content ?? []);
        setTotalRecord(response.data.totalElements);
        setLoader(false);
      }
    } catch {
      setNotification(failed('Failed to fetch client contacts'));
    }
  }, [clientId, searchQuery, sortModel, pageNo, pageSize, setNotification]);

  useEffect(() => {
    getClientContacts();
  }, [getClientContacts]);

  useEffect(() => {
    fetchColumnSettings(ColumnSettings.CLIENT_CLIENTCONTACT, ['firstName', 'action']).then(
      (res) => res && setSettings(res)
    );
  }, []);

  clientContacts?.map((contact) => {
    if (owns)
      tableData.push({
        ...contact,
        id: contact.id,
        firstName: `${contact.firstName} ${contact.lastName ?? ''}`,
        createdBy: contact.createdByName ?? contact.createdBy,
        createdOn: contact.createdOn
          ? getDateUsingFormat(contact.createdOn?.toString(), 'DD-MMM-YY')
          : '',
        markStatus: contact.markStatus,
        phoneNumber: contact.phoneNumber,
        position: contact.position,
        emailAddress: contact.emailAddress,
        supervisor:
          contact?.supervisor && fetchNameAndLinkedUrlsOfClientContact(contact?.supervisor),
        supervisorPosition: contact.supervisor?.position,
        linkedinProfile: contact.linkedinProfile,
        tags: contact.tags ?? [],
        location: !isEmpty(contact.location)
          ? `${contact.location?.city ?? ''} ${contact.location?.state ?? ''} ${
              contact.location?.country ?? ''
            }`
          : '',
        owner: contact.owner,
      });
    else
      tableData.push({
        id: contact.id,
        firstName: `${contact.firstName} ${contact.lastName}`,
        createdBy: contact.createdByName ?? contact.createdBy,
        createdOn: contact.createdOn
          ? getDateUsingFormat(contact.createdOn?.toString(), 'DD-MMM-YY')
          : '',
        markStatus: contact.markStatus,
        position: contact.position,
        emailAddress: contact.emailAddress,
        supervisor:
          contact?.supervisor && fetchNameAndLinkedUrlsOfClientContact(contact?.supervisor),
        supervisorPosition: contact.supervisor?.position,
        linkedinProfile: contact.linkedinProfile,
        phoneNumber: contact.phoneNumber,
        tags: contact.tags ?? [],
        location: !isEmpty(contact.location)
          ? `${contact.location?.city ?? ''} ${contact.location?.state ?? ''} ${
              contact.location?.country ?? ''
            }`
          : '',
        owner: contact.owner,
      });
  });

  const handleClientContactStatus = async (id: string, markStatus: boolean, firstName: string) => {
    clientsAmplitudeEvents(
      'client_details_contact_status_update',
      'Client Details Contact Status Update',
      workspaceContext
    );
    await updateClientContactStatus(id, markStatus);
    const avPayload = { ...activityLogDTOHelper };
    avPayload.action = LogAction.Edit;
    avPayload.description = `Marked status as ${
      markStatus === true ? 'Active' : 'Inactive'
    } for ${firstName} Client Contact under`;
    avPayload.parentEntitiyType = LogContextType.Client;
    avPayload.entityType = LogEntityType.ClientContact;
    avPayload.entityId = id;
    avPayload.parentEntitiyId = clientId;
    avPayload.field = `Client Contact`;
    avPayload.contextData = { clientId: clientId };
    await saveActivityLog(avPayload);

    getClientContacts();
  };

  const generateContactUrl = (id) => {
    return `/${id}/client-contact-details?isChild=${isChild}&name=${
      parentName ?? ''
    }&d=${queryData}&breadCrumbList=${
      param.get('breadCrumbList')
        ? getEncodedBreadCrumbs(
            getDecodedQueryParams(param.get('breadCrumbList') ?? '{}') as IBreadCrumbsList[],
            clintName
          )
        : getEncodedQueryParams(ClintPageBreadCrumb['CLIENT_CONTACTS'])
    }`;
  };

  const columns: GridColDef[] = [
    {
      field: 'firstName',
      headerName: 'Contact Name',
      flex: 0.8,
      renderCell: (params) => (
        <>
          {params.row['firstName'] && !/.*xxxxxx.*/.test(params.row['firstName']) ? (
            params.row['linkedinProfile'] ? (
              <Stack direction="row" spacing={1} alignItems="center">
                <WsLink to={generateContactUrl(params.row['id'])}>
                  <WsTypography variant={WsTypoVariants.SUB_TITLE}>
                    {params.row['firstName']}
                  </WsTypography>
                </WsLink>
                <Tooltip title="Navigate to LinkedIn Profile" placement="top">
                  <IconButton onClick={() => openUrlInNewTab(`${params.row['linkedinProfile']}`)}>
                    <WsIconLinkedIn />
                  </IconButton>
                </Tooltip>
              </Stack>
            ) : (
              <WsLink to={generateContactUrl(params.row['id'])}>
                <WsTypography variant={WsTypoVariants.SUB_TITLE}>
                  {params.row['firstName']}
                </WsTypography>
              </WsLink>
            )
          ) : (
            <WsTypography variant={WsTypoVariants.SUB_TITLE}>
              {params.row['firstName']}
            </WsTypography>
          )}
        </>
      ),
    },
    {
      field: 'markStatus',
      headerName: 'Status',
      flex: 0.8,
      renderCell: (params) => {
        const isInactive = params.row.markStatus === true || params.row.markStatus === 'true';
        const status = isInactive ? 'Active' : 'Inactive';
        return <WsTypography>{status}</WsTypography>;
      },
    },
    {
      field: 'position',
      headerName: 'Designation',
      flex: 0.8,
    },
    {
      field: 'location',
      headerName: 'Location',
      flex: 0.7,
    },
    {
      field: 'emailAddress',
      headerName: 'Email',
      flex: 1,
    },
    {
      field: 'phoneNumber',
      headerName: 'Contact',
      flex: 0.7,
    },
    {
      field: 'owner',
      headerName: 'Owner',
      flex: 0.8,
      renderCell: (params) => (
        <WsLink
          to={`/users/details-page?email=${params.row['owner']?.email}&isLink=true&isUserAnalytics=true`}
        >
          <WsTypography variant={WsTypoVariants.SUB_TITLE}>
            {params.row['owner']?.name}
          </WsTypography>
        </WsLink>
      ),
    },
    {
      field: 'tags',
      headerName: 'Tags',
      flex: 1,
    },
    {
      field: 'createdBy',
      headerName: 'Created By',
      flex: 1,
      renderCell: (params) => (
        <WsLink
          to={`/users/details-page?email=${params.row['createdBy']}&isLink=true&isUserAnalytics=true`}
        >
          <WsTypography variant={WsTypoVariants.SUB_TITLE}>{params.row['createdBy']}</WsTypography>
        </WsLink>
      ),
    },
    {
      field: 'createdOn',
      headerName: 'Created On',
      flex: 0.8,
    },
    {
      field: 'action',
      headerName: '',
      sortable: false,
      disableColumnMenu: true,
      width: 40,
      renderCell: (params) => {
        const isInDnc = params.row.dncDetails?.isInDnc ?? false;
        return (
          <>
            {isAuthorizedEditContact && (
              <PopoverOnClick element={<WsIconMoreVer />}>
                <Grid pt={1} px={2} columnGap={1}>
                  <WsIconText
                    text="Create Note"
                    textCursor="pointer"
                    onClick={() => {
                      clientsAmplitudeEvents(
                        'client_details_contact_note_add',
                        'Client Details Contact Note Add',
                        workspaceContext
                      );
                      openNotesFormDialog({
                        clientId: clientId,
                        contactId: params.id as string,
                        clientName: params.row['firstName'],
                        contactName: params.row['firstName'],
                        isClient: false,
                        openDialog: openDialog,
                        closeDialog: closeDialog,
                      });
                    }}
                    icon={<></>}
                  />
                </Grid>
                {isAuthorizedAddAssignClientContact && !isInDnc && (
                  <Stack pt={1} px={2} columnGap={1}>
                    <WsIconText
                      text="Assign Users"
                      textCursor="pointer"
                      onClick={() => {
                        clientsAmplitudeEvents(
                          'client_details_contact_assign',
                          'Client Details Contact Assign',
                          workspaceContext
                        );
                        setOpen(true);
                        setClientContactId(params.id as string);
                      }}
                      icon={<></>}
                    />
                  </Stack>
                )}
                {!isInDnc && (
                  <Stack pt={1} pb={1} px={2} columnGap={1}>
                    <WsIconText
                      text={`Mark as ${params.row.markStatus ? 'Inactive' : 'Active'}`}
                      textCursor="pointer"
                      onClick={() =>
                        handleClientContactStatus(
                          params.row.id,
                          params.row.markStatus ? false : true,
                          params.row.firstName
                        )
                      }
                      icon={<></>}
                    />
                  </Stack>
                )}
                <Stack pb={1} px={2} columnGap={1}>
                  <WsIconText
                    text="Jobs"
                    textCursor="pointer"
                    onClick={() => onNavigateToJobs(params.row['emailAddress'])}
                    icon={<></>}
                  />
                </Stack>
                {!isInDnc && (
                  <Stack pb={1} px={2} columnGap={1}>
                    <WsIconText
                      text="Edit"
                      textCursor="pointer"
                      onClick={() => onEditClientContact(params.row.id)}
                      icon={<></>}
                    />
                  </Stack>
                )}
                <Grid pb={1} px={2} columnGap={1}>
                  <WsIconText
                    text="Update Owner"
                    textCursor="pointer"
                    onClick={() => {
                      setOpenClientOwnerFormDialog(true);
                      setSelectedContact(params.row);
                    }}
                    icon={<></>}
                  />
                </Grid>
                <Grid pb={1} px={2} columnGap={1}>
                  <WsIconText
                    text={isInDnc ? 'Remove DNC' : 'Mark DNC'}
                    textCursor="pointer"
                    icon={<></>}
                    onClick={() => {
                      setSelectedContact(params.row);
                      setOpenDNC(!openDNC);
                    }}
                  />
                </Grid>
              </PopoverOnClick>
            )}
          </>
        );
      },
    },
  ];

  const handleDncSubmit = async (contactId: string, data: DncDetailsDTO) => {
    try {
      await assignDncDetails(contactId, data);
    } catch (error) {
      console.error('Error updating DNC details / lead status:', error);
    }
  };

  const clearFilter = () => setSearchQuery({ ...contactsDefaultFilterValues, search: '' });

  const handleCloseDialog = () => {
    getClientContacts();
    setOpenDialogs(initialState);
  };

  const onAddButtonClick = () => {
    clientsAmplitudeEvents(
      'client_details_contact_add',
      'Client Details Contact Add',
      workspaceContext
    );
    setOpenDialogs({
      ...openDialogs,
      openAddOrEditDialog: true,
    });
  };

  const fetchDataByIndex = (index: string) => {
    const getData = clientContacts.filter((data) => data.id == index);
    if (isEmpty(getData)) {
      setNotification(failed('Something went wrong, Please try again !!'));
    } else {
      const selectedContact = getData[0];
      setSelectedClientContact({
        ...selectedContact,
        tags: selectedContact.tags ?? [],
      });
    }
  };

  const onEditClientContact = (index: string) => {
    clientsAmplitudeEvents(
      'client_details_contact_edit',
      'Client Details Contact Edit',
      workspaceContext
    );
    fetchDataByIndex(index);
    setOpenDialogs({ ...openDialogs, editFlag: true, openAddOrEditDialog: true });
  };

  const closeDeleteDialog = () => setOpenDialogs({ ...openDialogs, openDeleteDialog: false });

  const handleDelete = async () => {
    if (selectedClientContact?.id) {
      try {
        await deleteClientContact(selectedClientContact.id);
        handleCloseDialog();
        setNotification(success('Client Contact has been deleted successfully !!'));
      } catch {
        setNotification(failed('Something went wrong, Please try again !!'));
      }
    }
  };

  const onNavigateToJobs = async (email: string) => {
    clientsAmplitudeEvents(
      'client_details_contact_jobs',
      'Client Details Contact Jobs',
      workspaceContext
    );
    openUrlInNewTab(
      `/jobs?d=${getEncodedQueryParams({
        ...defaultQuery,
        clientRecruiterList: [{ emailAddress: email ?? '' }],
      })}`
    );
  };

  const onClientContactOwnerUpdate = async (updatedContact: ClientContactDTO) => {
    const oldData = clientContacts.find((contact) => contact.id === updatedContact.id);
    if (oldData) {
      await createClientContactOwnerActivityLog(oldData, updatedContact);
      oldData.owner = updatedContact.owner;
    }
    setOpenClientOwnerFormDialog(false);
  };

  return (
    <>
      <Grid
        item
        container
        xs={12}
        justifyContent={owns ? 'flex-end' : 'space-between'}
        alignItems="center"
        mb={1.5}
      >
        <Grid item>
          {isAuthorizedAddContact && (
            <WsButton
              fullWidth
              onClick={onAddButtonClick}
              variant="contained"
              size="medium"
              startIcon={<AddOutlinedIcon />}
            >
              Add Contact
            </WsButton>
          )}
        </Grid>
      </Grid>
      <StyledGrid>
        <ContactFilters
          getUpdatedFiltersFromFilters={(query) => setSearchQuery(query)}
          searchQuery={searchQuery}
          tags={tags}
          clear={clearFilter}
          onAddButtonClick={onAddButtonClick}
          isClientContactTab={true}
        />
        {!loader ? (
          <>
            <Grid xs={12}>
              {settings != null && clientContacts?.length > 0 ? (
                <WsDataGrid
                  checkboxSelection
                  autoHeight
                  rows={tableData}
                  columns={columns}
                  paginationMode="server"
                  page={pageNo}
                  onPageChange={setPageNo}
                  pageSize={pageSize}
                  onPageSizeChange={setPageSize}
                  totalRows={totalRecord}
                  sortModel={sortModel}
                  onSortModelChange={(newSortModel) => {
                    setPageNo(0);
                    setSortModel(newSortModel);
                  }}
                  sortingMode="server"
                  selectionModel={rowSelectionModel}
                  onSelectionModelChange={(newSelectionModel) =>
                    setRowSelectionModel(newSelectionModel)
                  }
                  columnVisibility
                  columnSettings={settings}
                  onColumnSettingsChange={(data) => {
                    saveColumnSettingsToDB(
                      data,
                      ColumnSettings.CLIENT_CLIENTCONTACT,
                      setNotification
                    );
                    setSettings(data);
                  }}
                />
              ) : (
                <NoDataFoundCard
                  text={
                    isAuthorizedAddContact
                      ? 'Click on +Add Client Contact to add Client Contacts'
                      : 'No Client Contact available for the client'
                  }
                  icon={<EmptyTeamsAndUsersIcon />}
                  subElement={
                    <>
                      {isAuthorizedAddContact && (
                        <AddEntityButton
                          buttonText="Add Client Contact"
                          onClick={onAddButtonClick}
                          justifyContent="center"
                        />
                      )}
                    </>
                  }
                />
              )}
            </Grid>
            {rowSelectionModel.length > 0 && (
              <Grid item xs={12}>
                <Box mt={16}>
                  <BottomBlueBar sx={{ boxShadow: 12 }}>
                    <Stack direction="row" spacing={2} alignItems="baseline">
                      <WsTypography color={WsTypoColors.WHITE}>
                        {rowSelectionModel.length} entity(s) selected
                      </WsTypography>
                      <WsButton
                        variant="outlined"
                        onClick={() =>
                          setRowSelectionModel(tableData.map(({ id }) => id as GridRowId))
                        }
                      >
                        Select All
                      </WsButton>
                      <WsButton variant="outlined" onClick={() => setRowSelectionModel([])}>
                        Unselect All
                      </WsButton>
                      <Button
                        variant="contained"
                        style={{ marginLeft: 'auto' }}
                        onClick={() => {
                          openDrawer({
                            children: (
                              <Box p={2} mb={4}>
                                <ClientUserList
                                  clientId={clientId}
                                  clientContactIds={rowSelectionModel as []}
                                  onAssign={() => closeDrawer()}
                                  moduleName="CLIENT-CONTACT"
                                />
                              </Box>
                            ),
                            size: 'small',
                            title: 'Client Contacts Access',
                          });
                        }}
                      >
                        Assign Users
                      </Button>
                    </Stack>
                  </BottomBlueBar>
                </Box>
              </Grid>
            )}
          </>
        ) : (
          <Box
            sx={{
              '& .MuiCard-root': {
                boxShadow: 'none',
                elevation: 0,
              },
            }}
          >
            {loader ? (
              <WsListingLoader />
            ) : (
              isAuthorizedAddContact && (
                <NoDataFoundCard
                  text="Click on +Add Client Contact to add Client Contacts"
                  icon={<EmptyTeamsAndUsersIcon />}
                  subElement={
                    <AddEntityButton
                      buttonText="Add Client Contact"
                      onClick={onAddButtonClick}
                      justifyContent="center"
                    />
                  }
                />
              )
            )}
          </Box>
        )}
      </StyledGrid>
      {openDialogs.openAddOrEditDialog && (
        <ErrorBoundary FallbackComponent={({ error }) => <ErrorLogger error={error} />}>
          <WsDrawer
            open={openDialogs.openAddOrEditDialog}
            onClose={handleCloseDialog}
            title="Add client contacts"
          >
            <Box m={2}>
              <ClientContacts
                clientId={clientId}
                showSavedData={false}
                clientContactForEdit={
                  openDialogs.editFlag ? selectedClientContact : defaultClientContactDTO(clientId)
                }
                closeOnUpdate={handleCloseDialog}
                clientOwner={{
                  name: loggedInUser.firstName + ' ' + loggedInUser.lastName,
                  email: loggedInUser.email || '',
                }}
              />
            </Box>
          </WsDrawer>
        </ErrorBoundary>
      )}
      {openDialogs.openDeleteDialog && (
        <ErrorBoundary FallbackComponent={({ error }) => <ErrorLogger error={error} />}>
          <ConfirmDeleteDialog
            open={openDialogs.openDeleteDialog}
            onClose={closeDeleteDialog}
            onConfirm={handleDelete}
            deletingItem={`${selectedClientContact?.firstName} ${selectedClientContact?.lastName}`}
          />
        </ErrorBoundary>
      )}
      {open && (
        <WsDrawer
          open={open}
          onClose={reload}
          title={'Client Contacts Access'}
          headerSize="medium"
          size="small"
        >
          <ClientSharingTable
            clientId={clientId}
            moduleName="CLIENT-CONTACT"
            clientContactId={clientContactId}
          />
        </WsDrawer>
      )}
      {openDNC && selectedContact && (
        <WsDrawer open={openDNC} onClose={() => setOpenDNC(false)}>
          <DataPrivacy
            email={selectedContact.emailAddress ?? ''}
            candidateName={selectedContact.createdByName ?? ''}
            isInDnc={selectedContact.dncDetails?.isInDnc ?? false}
            dncDetails={selectedContact.dncDetails}
            privacyOptions={limitedPrivacyOptions}
            restrictLabel="DNC Up to *"
            entityId={(selectedContact?.id as string) ?? ''}
            parentEntitiyId={(selectedContact?.clientId as string) ?? ''}
            entityName={selectedContact?.firstName}
            logContextType={LogContextType.Client}
            onSubmitApi={(data) => handleDncSubmit(selectedContact?.id as string, data)}
            enableActivityLog={true}
          />
        </WsDrawer>
      )}
      {openClientOwnerFormDialog && selectedContact && selectedContact.id && (
        <ClientOwnerPopUp
          id={selectedContact.id}
          entityType="contact"
          clientOwner={selectedContact.owner || null}
          open={openClientOwnerFormDialog}
          onCancel={() => {
            setOpenClientOwnerFormDialog(false);
            setSelectedContact(null);
          }}
          onClientContactSave={onClientContactOwnerUpdate}
        />
      )}
    </>
  );
};

export default ClientContactsTab;
